/* Invoice Detail Page (Iter 95 #2) — Billit-style.

   Route: /accounting/invoices/:id
   Sol panel: 4 kart (Karsi taraf / Fatura bilgileri / Odeme bilgileri / Odemeler)
   Sag panel: PDF/scan dosyalari (upload + indir + sil + onizleme)
   Inline edit: her karttta "Duzenle" butonu.
*/
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ArrowLeft, Edit2, Save, X, Upload, FileText, Download, Trash2,
    Building2, ExternalLink, Loader2, CheckCircle2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_LABEL = {
    sales: 'Satis Faturasi',
    purchase: 'Alis Faturasi',
    sales_credit: 'Satis Iadesi',
    purchase_credit: 'Alis Iadesi',
};
const STATUS_TONE = {
    paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    partially_paid: 'bg-amber-100 text-amber-800 border-amber-200',
    open: 'bg-sky-100 text-sky-800 border-sky-200',
    overdue: 'bg-rose-100 text-rose-800 border-rose-200',
    cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
    draft: 'bg-gray-100 text-gray-700 border-gray-200',
};
const STATUS_LABEL = {
    paid: 'Odendi', partially_paid: 'Kismi Odendi', open: 'Acik',
    overdue: 'Gecikmis', cancelled: 'Iptal', draft: 'Taslak',
};

function Card({ title, onEdit, editing, onSave, onCancel, saving, children, testId }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={testId}>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">{title}</h3>
                {editing ? (
                    <div className="flex items-center gap-1">
                        <button onClick={onCancel} disabled={saving}
                            className="h-7 px-2 text-xs text-gray-600 hover:bg-gray-100 rounded flex items-center gap-1">
                            <X size={12} /> Iptal
                        </button>
                        <button onClick={onSave} disabled={saving}
                            className="h-7 px-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded flex items-center gap-1"
                            data-testid={`${testId}-save`}>
                            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                            Kaydet
                        </button>
                    </div>
                ) : (
                    onEdit && (
                        <button onClick={onEdit}
                            className="h-7 px-2 text-xs text-gray-600 hover:bg-gray-100 rounded flex items-center gap-1"
                            data-testid={`${testId}-edit`}>
                            <Edit2 size={12} /> Duzenle
                        </button>
                    )
                )}
            </div>
            {children}
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
            <span className="text-gray-500 shrink-0">{label}</span>
            <span className="text-gray-900 text-right break-words">{children || '—'}</span>
        </div>
    );
}

function Input(props) {
    return (
        <input
            {...props}
            className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
        />
    );
}

export default function InvoiceDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [inv, setInv] = useState(null);
    const [files, setFiles] = useState([]);
    const [matched, setMatched] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingCard, setEditingCard] = useState(null);
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/accounting/invoices/${id}`, { headers: hdrs() });
            setInv(r.data);
            const [filesRes, matchRes] = await Promise.all([
                axios.get(`${API}/api/accounting/invoices/${id}/files`, { headers: hdrs() }),
                axios.get(`${API}/api/accounting/invoices/${id}/matched-transactions`, { headers: hdrs() })
                    .catch(() => ({ data: { items: [] } })),
            ]);
            let fileItems = filesRes.data.items || [];
            // Iter 96: Billit kaynakli fatura + dosya yoksa otomatik PDF cek
            if (fileItems.length === 0 && r.data?.billit_id) {
                try {
                    const sync = await axios.post(
                        `${API}/api/accounting/invoices/${id}/files/sync-billit`,
                        {}, { headers: hdrs() },
                    );
                    if ((sync.data?.added || 0) > 0) {
                        const r2 = await axios.get(`${API}/api/accounting/invoices/${id}/files`, { headers: hdrs() });
                        fileItems = r2.data.items || [];
                    }
                } catch { /* silent — kullanici manuel ile cekebilir */ }
            }
            setFiles(fileItems);
            setMatched(matchRes.data.items || matchRes.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fatura yuklenemedi');
            navigate('/accounting?tab=invoices');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);
    useEffect(() => { load(); }, [load]);

    // PDF preview blob (auth gerektiriyor — iframe'e direkt URL veremeyiz)
    useEffect(() => {
        const pdf = files.find((f) => (f.mime || '').includes('pdf'));
        if (!pdf) {
            setPreviewUrl(null);
            setPreviewFile(null);
            return;
        }
        if (previewFile && previewFile.id === pdf.id) return;
        let cancelled = false;
        let createdUrl = null;
        (async () => {
            try {
                const r = await axios.get(
                    `${API}/api/accounting/invoices/${id}/files/${pdf.id}/download`,
                    { headers: hdrs(), responseType: 'blob' },
                );
                if (cancelled) return;
                createdUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                setPreviewUrl(createdUrl);
                setPreviewFile(pdf);
            } catch (e) {
                /* ignore preview errors */
            }
        })();
        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files, id]);

    const startEdit = (card, initial) => { setEditingCard(card); setDraft(initial); };
    const cancelEdit = () => { setEditingCard(null); setDraft({}); };
    const saveEdit = async (patch) => {
        setSaving(true);
        try {
            const r = await axios.patch(`${API}/api/accounting/invoices/${id}`, patch, { headers: hdrs() });
            setInv((cur) => ({ ...cur, ...r.data }));
            toast.success('Kaydedildi');
            cancelEdit();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kaydedilemedi');
        } finally {
            setSaving(false);
        }
    };

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await axios.post(`${API}/api/accounting/invoices/${id}/files`, fd, {
                headers: { ...hdrs(), 'Content-Type': 'multipart/form-data' }, timeout: 120000,
            });
            setFiles((cur) => [r.data, ...cur]);
            toast.success(`${file.name} yuklendi`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Yukleme hatasi');
        } finally {
            setUploading(false);
        }
    };

    const onDeleteFile = async (fileId) => {
        if (!window.confirm('Bu dosya silinsin mi?')) return;
        try {
            await axios.delete(`${API}/api/accounting/invoices/${id}/files/${fileId}`, { headers: hdrs() });
            setFiles((cur) => cur.filter((f) => f.id !== fileId));
            toast.success('Silindi');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Silinemedi');
        }
    };

    const downloadFile = async (file) => {
        try {
            const r = await axios.get(
                `${API}/api/accounting/invoices/${id}/files/${file.id}/download`,
                { headers: hdrs(), responseType: 'blob' },
            );
            const url = window.URL.createObjectURL(new Blob([r.data], { type: file.mime || 'application/octet-stream' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = file.filename || 'file';
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error('Indirilemedi');
        }
    };

    const syncBillitPdf = async () => {
        if (!inv?.billit_id) return;
        setUploading(true);
        try {
            const r = await axios.post(
                `${API}/api/accounting/invoices/${id}/files/sync-billit`,
                {}, { headers: hdrs() },
            );
            const d = r.data;
            if (d.added > 0) {
                const r2 = await axios.get(`${API}/api/accounting/invoices/${id}/files`, { headers: hdrs() });
                setFiles(r2.data.items || []);
                toast.success(`${d.added} dosya Billit'ten cekildi`);
            } else if (d.skipped > 0) {
                toast.info('Tum dosyalar zaten guncel');
            } else if (d.errors?.length) {
                toast.error(`Billit hatasi: ${d.errors[0]}`);
            } else {
                toast.info('Billit\'te bu faturanin ek dosyasi yok');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Billit sync hatasi');
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center text-gray-500">
                <Loader2 size={18} className="animate-spin mr-2" /> Fatura yukleniyor...
            </div>
        );
    }
    if (!inv) return null;

    const remaining = Math.max(0, Math.abs(Number(inv.total) || 0) - Number(inv.paid_amount || 0));

    return (
        <div className="min-h-screen bg-gray-50" data-testid="invoice-detail-page">
            {/* Top bar */}
            <div className="bg-white border-b sticky top-0 z-10">
                <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center gap-3">
                    <button onClick={() => navigate('/accounting?tab=invoices')}
                        className="p-2 hover:bg-gray-100 rounded-lg" data-testid="inv-back">
                        <ArrowLeft size={18} />
                    </button>
                    <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500">{TYPE_LABEL[inv.type] || inv.type}</div>
                        <div className="text-lg font-bold text-gray-900 truncate">
                            #{inv.invoice_number} · {inv.contact_name || 'Bilinmeyen'}
                        </div>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${STATUS_TONE[inv.status] || STATUS_TONE.open}`}
                        data-testid="inv-status-pill">
                        {STATUS_LABEL[inv.status] || inv.status}
                    </span>
                    <div className="text-right">
                        <div className="text-xs text-gray-500">Toplam</div>
                        <div className="text-lg font-bold text-gray-900" data-testid="inv-total">
                            {fmt(inv.total)} {inv.currency || 'EUR'}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-6 py-4 grid grid-cols-1 lg:grid-cols-[1fr_500px] gap-4">
                {/* LEFT: 4 kart */}
                <div className="space-y-3">
                    {/* Karsi taraf */}
                    <Card
                        title={inv.type?.startsWith('sales') ? 'Musteri' : 'Tedarikci'}
                        testId="inv-card-contact"
                        editing={editingCard === 'contact'}
                        saving={saving}
                        onEdit={() => startEdit('contact', {
                            contact_name: inv.contact_name || '',
                            vat_number: inv.vat_number || '',
                            iban: inv.iban || '',
                        })}
                        onCancel={cancelEdit}
                        onSave={() => saveEdit(draft)}
                    >
                        {editingCard === 'contact' ? (
                            <div className="space-y-2">
                                <Input value={draft.contact_name} onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })} placeholder="Firma adi" />
                                <Input value={draft.vat_number} onChange={(e) => setDraft({ ...draft, vat_number: e.target.value })} placeholder="KDV no" />
                                <Input value={draft.iban} onChange={(e) => setDraft({ ...draft, iban: e.target.value })} placeholder="IBAN" />
                            </div>
                        ) : (
                            <>
                                <Row label="Isim">
                                    <div className="flex items-center gap-2 justify-end">
                                        <span>{inv.contact_name}</span>
                                        {inv.contact_id && (
                                            <button
                                                onClick={() => navigate(`/accounting/companies/${inv.contact_id}`)}
                                                className="text-xs text-blue-600 hover:underline flex items-center gap-0.5"
                                                data-testid="inv-goto-company">
                                                <Building2 size={11} /> Cari
                                            </button>
                                        )}
                                    </div>
                                </Row>
                                <Row label="KDV No">{inv.vat_number}</Row>
                                <Row label="IBAN">{inv.iban}</Row>
                                <Row label="Para birimi">{inv.currency || 'EUR'}</Row>
                            </>
                        )}
                    </Card>

                    {/* Fatura Bilgileri */}
                    <Card
                        title="Fatura Bilgileri"
                        testId="inv-card-meta"
                        editing={editingCard === 'meta'}
                        saving={saving}
                        onEdit={() => startEdit('meta', {
                            invoice_number: inv.invoice_number,
                            invoice_date: inv.invoice_date,
                            due_date: inv.due_date,
                            notes: inv.notes,
                        })}
                        onCancel={cancelEdit}
                        onSave={() => saveEdit(draft)}
                    >
                        {editingCard === 'meta' ? (
                            <div className="space-y-2">
                                <Input value={draft.invoice_number} onChange={(e) => setDraft({ ...draft, invoice_number: e.target.value })} placeholder="Fatura no" />
                                <Input type="date" value={draft.invoice_date} onChange={(e) => setDraft({ ...draft, invoice_date: e.target.value })} />
                                <Input type="date" value={draft.due_date} onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} />
                                <textarea value={draft.notes || ''} onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                                    rows={3} placeholder="Notlar"
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
                            </div>
                        ) : (
                            <>
                                <Row label="Tip">{TYPE_LABEL[inv.type] || inv.type}</Row>
                                <Row label="No">{inv.invoice_number}</Row>
                                <Row label="Tarih">{inv.invoice_date}</Row>
                                <Row label="Vade">{inv.due_date}</Row>
                                <Row label="Ara toplam">{fmt(inv.subtotal)} €</Row>
                                <Row label="KDV">{fmt(inv.vat_amount)} €</Row>
                                <Row label="Toplam"><strong>{fmt(inv.total)} €</strong></Row>
                                {inv.notes && <Row label="Notlar"><span className="italic text-gray-600">{inv.notes}</span></Row>}
                            </>
                        )}
                    </Card>

                    {/* Odeme Bilgileri */}
                    <Card
                        title="Odeme Bilgileri"
                        testId="inv-card-payment"
                        editing={editingCard === 'payment'}
                        saving={saving}
                        onEdit={() => startEdit('payment', {
                            payment_reference: inv.payment_reference || '',
                            paid_amount: inv.paid_amount || 0,
                            status: inv.status || 'open',
                        })}
                        onCancel={cancelEdit}
                        onSave={() => saveEdit({
                            payment_reference: draft.payment_reference,
                            paid_amount: parseFloat(draft.paid_amount) || 0,
                            status: draft.status,
                        })}
                    >
                        {editingCard === 'payment' ? (
                            <div className="space-y-2">
                                <Input value={draft.payment_reference} onChange={(e) => setDraft({ ...draft, payment_reference: e.target.value })} placeholder="Odeme ref" />
                                <Input type="number" step="0.01" value={draft.paid_amount} onChange={(e) => setDraft({ ...draft, paid_amount: e.target.value })} placeholder="Odenen" />
                                <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                                    className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md">
                                    <option value="open">Acik</option>
                                    <option value="partially_paid">Kismi Odendi</option>
                                    <option value="paid">Odendi</option>
                                    <option value="overdue">Gecikmis</option>
                                    <option value="cancelled">Iptal</option>
                                </select>
                            </div>
                        ) : (
                            <>
                                <Row label="Odeme ref"><span className="font-mono text-xs">{inv.payment_reference}</span></Row>
                                <Row label="Odenen">{fmt(inv.paid_amount)} €</Row>
                                <Row label="Kalan"><strong className={remaining > 0.01 ? 'text-rose-700' : 'text-emerald-700'}>{fmt(remaining)} €</strong></Row>
                                <Row label="Durum">
                                    <span className={`text-xs px-2 py-0.5 rounded font-semibold border ${STATUS_TONE[inv.status] || STATUS_TONE.open}`}>
                                        {STATUS_LABEL[inv.status] || inv.status}
                                    </span>
                                </Row>
                            </>
                        )}
                    </Card>

                    {/* Odemeler (eslesmis banka tx'leri) */}
                    <Card title="Eslesmis Odemeler" testId="inv-card-matches">
                        {matched.length === 0 ? (
                            <div className="text-sm text-gray-400 py-3 text-center">Bu faturaya bagli banka hareketi yok</div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {matched.map((m) => (
                                    <div key={m.id} className="py-2 flex items-center justify-between text-sm" data-testid={`inv-match-${m.id}`}>
                                        <div className="min-w-0">
                                            <div className="text-gray-900 font-medium">{m.transaction_date} · {m.counterparty_name}</div>
                                            {m.communication && <div className="text-xs text-gray-500 truncate">{m.communication}</div>}
                                        </div>
                                        <div className={`text-sm font-bold shrink-0 ${(m.amount || 0) < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                                            {fmt(m.amount)} €
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </div>

                {/* RIGHT: PDF / Dosyalar */}
                <div className="space-y-3">
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide flex items-center gap-1.5">
                                <FileText size={14} /> Dosyalar ({files.length})
                            </h3>
                            <div className="flex items-center gap-1.5">
                                {inv?.billit_id && (
                                    <button
                                        onClick={syncBillitPdf}
                                        disabled={uploading}
                                        className="h-8 px-3 text-xs font-semibold rounded-lg flex items-center gap-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                                        data-testid="inv-billit-sync"
                                        title="Bu faturanin PDF'ini Billit'ten otomatik cek"
                                    >
                                        {uploading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                        Billit'ten Cek
                                    </button>
                                )}
                                <label className={`h-8 px-3 text-xs font-semibold rounded-lg cursor-pointer flex items-center gap-1.5 ${uploading ? 'bg-gray-200 text-gray-500' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
                                    data-testid="inv-upload-btn">
                                    {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                                    Yukle
                                    <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
                                        onChange={onUpload} disabled={uploading} data-testid="inv-upload-input" />
                                </label>
                            </div>
                        </div>
                        {files.length === 0 ? (
                            <div className="text-sm text-gray-400 py-8 text-center border-2 border-dashed border-gray-200 rounded-lg">
                                Bu faturaya henuz dosya eklenmemis
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {files.map((f) => (
                                    <div key={f.id} className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                                        data-testid={`inv-file-${f.id}`}>
                                        <FileText size={16} className="text-gray-400 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-gray-900 truncate">{f.filename}</div>
                                            <div className="text-[10px] text-gray-500">
                                                {(f.size_bytes / 1024).toFixed(1)} KB · {f.source}
                                            </div>
                                        </div>
                                        <button onClick={() => downloadFile(f)} className="p-1.5 hover:bg-gray-200 rounded text-gray-600"
                                            data-testid={`inv-file-dl-${f.id}`}>
                                            <Download size={13} />
                                        </button>
                                        <button onClick={() => onDeleteFile(f.id)} className="p-1.5 hover:bg-rose-100 text-rose-600 rounded"
                                            data-testid={`inv-file-del-${f.id}`}>
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* PDF inline preview (varsa) */}
                    {previewUrl && previewFile && (
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                            <div className="px-4 py-2 border-b text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                                <CheckCircle2 size={12} className="text-emerald-600" /> Onizleme: {previewFile.filename}
                                <a href={previewUrl} target="_blank" rel="noreferrer"
                                    className="ml-auto text-blue-600 hover:underline flex items-center gap-0.5">
                                    <ExternalLink size={11} /> Yeni sekmede ac
                                </a>
                            </div>
                            <iframe
                                src={previewUrl}
                                title="pdf"
                                className="w-full h-[600px] bg-gray-100"
                                data-testid="inv-pdf-iframe"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
